import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { 
  IconButton, 
  Tooltip as MuiTooltip, 
  Badge, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Chip, 
  OutlinedInput, 
  TextField,
  InputAdornment,
  Autocomplete,
  Box,
  Button
} from '@mui/material';
import { Search, FilterList, Clear } from '@mui/icons-material';
import { createTaskIcon, myTasksIcon, excelIcon } from '../../common/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import TaskCreationModal from '../TaskCreationModal';
import ViewTasksModal from '../ViewTasksModal';
import { DetailView } from '../DetailView/DetailView';
import { Modules } from '../../common/constants/sidebar';
import { toast } from 'react-toastify';
import { toastOptions } from '../../common/utils/toasterOptions';
import { getTasksByProjectAllocation, getActiveTaskCountsBatch } from '../../http/requests/task';
import './BenchView.css';

const COLORS = {
  'Billable': '#0088FE',
  'Non-Billable': '#00C49F', 
  'Shadow': '#FFBB28',
  'billable': '#0088FE',
  'non-billable': '#00C49F',
  'shadow': '#FFBB28'
};

const BenchView = ({ benchResources, benchStreamData, allProjectAllocations = null }) => {
  // Enhance bench resources with correct lastAllocationEndDate from allProjectAllocations
  const enhancedBenchResources = useMemo(() => {
    if (!benchResources || !allProjectAllocations?.data) {
      return benchResources;
    }

    return benchResources.map(benchResource => {
      // Find matching allocation in allProjectAllocations by multiple criteria
      const matchingAllocation = allProjectAllocations.data.find(allocation => {
        // Try different matching strategies
        
        // 1. Match by zohoEmployeeId (most reliable for numeric IDs)
        if (allocation.zohoEmployeeId && benchResource.zohoEmployeeId && 
            String(allocation.zohoEmployeeId) === String(benchResource.zohoEmployeeId)) {
          return true;
        }
        
        // 2. Match by employeeId (GUID or numeric)
        if (allocation.employeeId && benchResource.employeeId && 
            String(allocation.employeeId) === String(benchResource.employeeId)) {
          return true;
        }
        
        // 3. Match by projectAllocationId
        if (allocation.projectAllocationId && benchResource.projectAllocationId && 
            allocation.projectAllocationId === benchResource.projectAllocationId) {
          return true;
        }
        
        // 4. Match by name (first + last name combination)
        if (allocation.firstName && allocation.lastName && benchResource.name) {
          const allocationFullName = `${allocation.firstName} ${allocation.lastName}`.trim();
          const benchResourceName = benchResource.name.trim();
          if (allocationFullName.toLowerCase() === benchResourceName.toLowerCase()) {
            return true;
          }
        }
        
        // 5. Match by name parts (handle case variations)
        if (benchResource.name && (allocation.firstName || allocation.lastName)) {
          const benchName = benchResource.name.toLowerCase().trim();
          const firstName = (allocation.firstName || '').toLowerCase().trim();
          const lastName = (allocation.lastName || '').toLowerCase().trim();
          
          // Check if bench name contains both first and last name
          if (firstName && lastName && benchName.includes(firstName) && benchName.includes(lastName)) {
            return true;
          }
        }
        
        return false;
      });

      if (matchingAllocation) {
        // If lastProject is "N/A", this means there's no previous project, 
        // so lastAllocationEndDate should also be "N/A" regardless of current project data
        if (benchResource.lastProject === "N/A" || benchResource.lastProject === null) {
          return {
            ...benchResource,
            lastAllocationEndDate: "N/A",
            originalEndDate: benchResource.lastAllocationEndDate || benchResource.endDate,
            matchedBy: 'allProjectAllocations'
          };
        }
        
        // Only use the allocation's lastAllocationEndDate if it exists and is different from current endDate
        // This ensures we're getting the actual "last" allocation end date, not the current one
        const dateToUse = matchingAllocation.lastAllocationEndDate && 
                         matchingAllocation.lastAllocationEndDate !== matchingAllocation.endDate ? 
                         matchingAllocation.lastAllocationEndDate : 
                         matchingAllocation.lastAllocationEndDate;
        
        return {
          ...benchResource,
          lastAllocationEndDate: dateToUse || "N/A",
          // Also preserve other useful fields from the allocation
          originalEndDate: benchResource.lastAllocationEndDate || benchResource.endDate,
          matchedBy: 'allProjectAllocations'
        };
      }

      // If no match found, return original resource but try to use existing date fields
      const existingDate = benchResource.lastAllocationEndDate || 
                          benchResource.LastAllocationEndDate || 
                          benchResource.endDate || 
                          null;
      
      // If lastProject is "N/A", set lastAllocationEndDate to "N/A" as well
      const finalDate = (benchResource.lastProject === "N/A" || benchResource.lastProject === null) ? "N/A" : existingDate;
      
      return {
        ...benchResource,
        matchedBy: 'noMatch',
        // Preserve any existing lastAllocationEndDate from original bench data
        lastAllocationEndDate: finalDate
      };
    });
  }, [benchResources, allProjectAllocations]);
  // Data validation
  useEffect(() => {
    // Data processing validation
    if (enhancedBenchResources && enhancedBenchResources.length > 0) {
      const matchedCount = enhancedBenchResources.filter(r => r.matchedBy === 'allProjectAllocations').length;
      const noMatchCount = enhancedBenchResources.filter(r => r.matchedBy === 'noMatch').length;

      // Data processing complete
    }
  }, [enhancedBenchResources, allProjectAllocations]);

  // Main View Mode State
  const [mainViewMode, setMainViewMode] = useState('bench'); // 'bench' or 'shadow-nonbillable'
  
  // Sorting state
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // Task Modal state
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [selectedEmployeeForTask, setSelectedEmployeeForTask] = useState(null);
  
  // View Tasks Modal state
  const [viewTasksModalOpen, setViewTasksModalOpen] = useState(false);
  const [selectedEmployeeForViewTasks, setSelectedEmployeeForViewTasks] = useState(null);

  // Simplified Workflow State - direct employee to task count mapping
  const [employeeToTaskCountMap, setEmployeeToTaskCountMap] = useState(new Map());
  const [batchLoadingComplete, setBatchLoadingComplete] = useState(false);

  // Project Allocations Detail View Modal state
  const [viewProjectDetails, setViewProjectDetails] = useState(false);
  const [selectedEmployeeIdForDetail, setSelectedEmployeeIdForDetail] = useState('');

  // LastProject Filter state
  const [selectedLastProjects, setSelectedLastProjects] = useState([]);
  const [lastProjectSearchText, setLastProjectSearchText] = useState('');

  // Shadow/Non-Billable Filter state
  const [selectedBillStatuses, setSelectedBillStatuses] = useState([]);

  const formatExperience = (totalExperienceInMonths) => {
    // Handle null, undefined, empty string, or NaN
    if (totalExperienceInMonths === null || 
        totalExperienceInMonths === undefined || 
        totalExperienceInMonths === '' || 
        isNaN(totalExperienceInMonths)) {
      return 'N/A';
    }
    
    const months = parseInt(totalExperienceInMonths);
    
    if (months === 0) return '0 months';
    
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years === 0) {
      return `${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    } else if (remainingMonths === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    } else {
      return `${years}.${Math.round((remainingMonths / 12) * 10)} years`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A' || dateString === null || dateString === undefined) {
      return 'N/A';
    }
    
    try {
      let date;
      
      // Handle different date formats
      if (typeof dateString === 'string') {
        if (dateString.includes('T')) {
          // ISO format: "2025-08-31T00:00:00" - treat as local date, not UTC
          // Extract just the date part to avoid timezone issues
          const datePart = dateString.split('T')[0]; // "2025-08-31"
          const [year, month, day] = datePart.split('-').map(Number);
          date = new Date(year, month - 1, day); // month is 0-indexed in JS Date
        } else {
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      // Use local date methods (not UTC) since we parsed as local date
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const formatted = `${monthNames[month]} ${day}, ${year}`;
      return formatted;
    } catch (error) {
      return 'N/A';
    }
  };

  // Helper function to get employee name from different data structures
  const getEmployeeName = (resource) => {
    // Try different name combinations based on data source
    if (resource.name) return resource.name;
    if (resource.employeeName) return resource.employeeName;
    if (resource.firstName && resource.lastName) {
      return `${resource.firstName} ${resource.lastName}`.trim();
    }
    if (resource.firstName) return resource.firstName;
    if (resource.lastName) return resource.lastName;
    return 'N/A';
  };

  // Helper function to get employee ID from different data structures (integer ID, not GUID)
  const getEmployeeId = (resource) => {
    // Prefer integer employee IDs over GUIDs
    if (resource.zohoEmployeeId && !isNaN(resource.zohoEmployeeId)) return resource.zohoEmployeeId;
    if (resource.empId && !isNaN(resource.empId)) return resource.empId;
    if (resource.employeeNumber && !isNaN(resource.employeeNumber)) return resource.employeeNumber;
    if (resource.employeeCode && !isNaN(resource.employeeCode)) return resource.employeeCode;
    
    // Only use employeeId if it's numeric (not a GUID)
    if (resource.employeeId && !isNaN(resource.employeeId)) return resource.employeeId;
    
    // If all else fails, look for any numeric field that could be employee ID
    const numericFields = ['id', 'empNumber', 'employeeNumber'];
    for (const field of numericFields) {
      if (resource[field] && !isNaN(resource[field])) return resource[field];
    }
    
    return 'N/A';
  };

  // Helper function to get project name from different data structures
  const getProjectName = (resource) => {
    if (resource.projectName) return resource.projectName;
    if (resource.project) return resource.project;
    return 'N/A';
  };

  // Helper function to get LastAllocationEndDate from the API response
  const getLastAllocationEndDate = (resource) => {
    // CRITICAL: If lastProject is "N/A", then lastAllocationEndDate should also be "N/A"
    // This handles the case where a person has only ongoing projects and no previous allocations
    if (resource.lastProject === 'N/A' || resource.lastProject === null) {
      return 'N/A';
    }
    
    // Priority 1: Use the enhanced lastAllocationEndDate from matching
    if (resource.lastAllocationEndDate && 
        resource.lastAllocationEndDate !== null && 
        resource.lastAllocationEndDate !== 'N/A' &&
        resource.lastAllocationEndDate !== '') {
      return resource.lastAllocationEndDate;
    }
    
    // Priority 2: Try other possible field variations
    const possibleFields = [
      'LastAllocationEndDate', 
      'lastallocationenddate',
      'LASTALLOCATIONENDDATE',
      'last_allocation_end_date'
    ];
    
    for (const field of possibleFields) {
      if (resource[field] && 
          resource[field] !== null && 
          resource[field] !== 'N/A' && 
          resource[field] !== '') {
        return resource[field];
      }
    }
    
    // Priority 3: Use endDate only if it's not the default 2025-12-31 and not null
    // BUT only if we have a valid lastProject (not current project end date)
    if (resource.endDate && 
        resource.endDate !== null && 
        resource.endDate !== 'N/A' && 
        resource.endDate !== '' &&
        !(typeof resource.endDate === 'string' && resource.endDate.includes('2025-12-31'))) {
      return resource.endDate;
    }
    
    // Priority 4: Use originalEndDate if available (from before enhancement)
    if (resource.originalEndDate && 
        resource.originalEndDate !== null && 
        resource.originalEndDate !== 'N/A' && 
        resource.originalEndDate !== '' &&
        !(typeof resource.originalEndDate === 'string' && resource.originalEndDate.includes('2025-12-31'))) {
      return resource.originalEndDate;
    }
    
    // If we reach here and have no valid last allocation end date, return N/A
    return 'N/A';
  };

  // Sorting function
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get unique last projects for filter options
  const uniqueLastProjects = useMemo(() => {
    if (!enhancedBenchResources) return [];
    
    const projects = enhancedBenchResources
      .map(resource => resource.lastProject)
      .filter(project => project && project !== 'N/A' && project.trim() !== '')
      .map(project => project.trim());
    
    return [...new Set(projects)].sort();
  }, [enhancedBenchResources]);

  // Get Shadow and Non-Billable resources from ALL project allocations (not just bench)
  const shadowNonBillableResources = useMemo(() => {
    // Use allProjectAllocations if available, otherwise fall back to enhancedBenchResources
    const dataSource = allProjectAllocations?.data || enhancedBenchResources || [];
    
    if (!dataSource || dataSource.length === 0) return [];
    
    const filtered = dataSource.filter(resource => {
      if (!resource.billStatus) return false;
      
      const billStatus = resource.billStatus.toLowerCase().trim();
      const isMatch = billStatus === 'shadow' || 
                     billStatus === 'non-billable' ||
                     billStatus === 'non billable' ||
                     billStatus === 'nonbillable' ||
                     billStatus.startsWith('nb-') ||
                     billStatus.startsWith('nb ') ||
                     (billStatus.includes('non') && billStatus.includes('billable'));
      
      // Additional check: exclude bench projects for Shadow/Non-Billable view
      if (isMatch && allProjectAllocations) {
        const projectName = (resource.projectName || resource.project || '').toLowerCase();
        const isBenchProject = projectName.includes('bench') || 
                              projectName.includes('unallocated') || 
                              projectName.includes('available') ||
                              projectName === 'n/a' ||
                              projectName === '' ||
                              projectName === 'unknown project';
        
        if (isBenchProject) {
          return false;
        }
      }
      
      return isMatch;
    });
    
    return filtered;
  }, [allProjectAllocations, enhancedBenchResources]);

  // Get unique bill statuses for Shadow/Non-Billable filter
  const uniqueBillStatuses = useMemo(() => {
    if (!shadowNonBillableResources) return [];
    
    const statuses = shadowNonBillableResources
      .map(resource => resource.billStatus)
      .filter(status => status && status.trim() !== '')
      .map(status => status.trim());
    
    return [...new Set(statuses)].sort();
  }, [shadowNonBillableResources]);

  // Get filtered and sorted data
  const sortedBenchResources = useMemo(() => {
    if (!enhancedBenchResources) return enhancedBenchResources;

    // First apply filters
    let filtered = [...enhancedBenchResources];
    
    // Apply LastProject filter
    if (selectedLastProjects.length > 0) {
      filtered = filtered.filter(resource => 
        selectedLastProjects.includes(resource.lastProject)
      );
    }

    // Then apply sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
          case 'name':
            aValue = (a.name || '').toLowerCase();
            bValue = (b.name || '').toLowerCase();
            break;
          case 'employeeId':
            aValue = (a.employeeId || '').toLowerCase();
            bValue = (b.employeeId || '').toLowerCase();
            break;
          case 'stream':
            aValue = (a.stream || '').toLowerCase();
            bValue = (b.stream || '').toLowerCase();
            break;
          case 'designation':
            aValue = (a.designation || '').toLowerCase();
            bValue = (b.designation || '').toLowerCase();
            break;
          case 'totalExperience':
            // For experience, sort by numeric value
            aValue = parseInt(a.totalExperience) || 0;
            bValue = parseInt(b.totalExperience) || 0;
            break;
          case 'lastProject':
            aValue = (a.lastProject || '').toLowerCase();
            bValue = (b.lastProject || '').toLowerCase();
            break;
          case 'lastAllocationEndDate':
            aValue = new Date(getLastAllocationEndDate(a) || '1970-01-01');
            bValue = new Date(getLastAllocationEndDate(b) || '1970-01-01');
            break;
          default:
            return 0;
        }

        // Compare values
        if (sortField === 'totalExperience' || sortField === 'lastAllocationEndDate') {
          // Numeric/Date comparison
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        } else {
          // String comparison
          if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        }
      });
    }

    return filtered;
  }, [enhancedBenchResources, sortField, sortDirection, selectedLastProjects]);

  // Get filtered and sorted Shadow/Non-Billable resources
  const sortedShadowNonBillableResources = useMemo(() => {
    if (!shadowNonBillableResources) return shadowNonBillableResources;

    // First apply filters
    let filtered = [...shadowNonBillableResources];
    
    // Apply Bill Status filter
    if (selectedBillStatuses.length > 0) {
      filtered = filtered.filter(resource => 
        selectedBillStatuses.includes(resource.billStatus)
      );
    }

    // Then apply sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
          case 'name':
            aValue = getEmployeeName(a).toLowerCase();
            bValue = getEmployeeName(b).toLowerCase();
            break;
          case 'employeeId':
            aValue = getEmployeeId(a).toLowerCase();
            bValue = getEmployeeId(b).toLowerCase();
            break;
          case 'stream':
            aValue = (a.stream || '').toLowerCase();
            bValue = (b.stream || '').toLowerCase();
            break;
          case 'designation':
            aValue = (a.designation || '').toLowerCase();
            bValue = (b.designation || '').toLowerCase();
            break;
          case 'projectName':
            aValue = getProjectName(a).toLowerCase();
            bValue = getProjectName(b).toLowerCase();
            break;
          case 'totalExperience':
            // For experience, sort by numeric value
            aValue = parseInt(a.totalExperience) || 0;
            bValue = parseInt(b.totalExperience) || 0;
            break;
          case 'billStatus':
            aValue = (a.billStatus || '').toLowerCase();
            bValue = (b.billStatus || '').toLowerCase();
            break;
          case 'startDate':
            aValue = new Date(a.startDate || '1970-01-01');
            bValue = new Date(b.startDate || '1970-01-01');
            break;
          case 'endDate':
            aValue = new Date(a.endDate || '1970-01-01');
            bValue = new Date(b.endDate || '1970-01-01');
            break;
          default:
            return 0;
        }

        // Compare values
        if (sortField === 'totalExperience' || sortField === 'startDate' || sortField === 'endDate') {
          // Numeric/Date comparison
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        } else {
          // String comparison
          if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        }
      });
    }

    return filtered;
  }, [shadowNonBillableResources, sortField, sortDirection, selectedBillStatuses]);

  // Sort indicator component
  const SortIndicator = ({ field }) => {
    if (sortField !== field) {
      return <span className="sort-indicator">⇅</span>;
    }
    return (
      <span className="sort-indicator active">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const getAverageAllocation = () => {
    if (!enhancedBenchResources || enhancedBenchResources.length === 0) return '0.0';
    
    const validAllocations = enhancedBenchResources.filter(r => 
      r.allocation !== null && r.allocation !== undefined && 
      !isNaN(r.allocation) && isFinite(r.allocation)
    );
    
    if (validAllocations.length === 0) return '0.0';
    
    const totalAllocation = validAllocations.reduce((sum, r) => sum + r.allocation, 0);
    return (totalAllocation / validAllocations.length * 100).toFixed(1);
  };

  // Function to count tasks by status for an employee
  const countActiveTasksForEmployee = (tasks) => {
    if (!Array.isArray(tasks)) return 0;
    
    return tasks.filter(task => {
      const status = (task.status || '').toLowerCase();
      return status === 'open' || status === 'in progress';
    }).length;
  };

  // Function to get task count for an employee using simplified direct mapping
  const getTaskCountForEmployee = (employee) => {
    const employeeIdToUse = employee.employeeGuid || employee.employeeId;
    // Normalize to lowercase for consistent matching - ensure it's a string first
    const normalizedId = employeeIdToUse ? String(employeeIdToUse).toLowerCase() : null;
    const taskCount = normalizedId ? (employeeToTaskCountMap.get(normalizedId) || 0) : 0;
    
    return taskCount;
  };

  // =====================================================
  // SIMPLIFIED WORKFLOW: Direct Employee Task Counts
  // =====================================================
  
  const fetchTaskCountsSimplifiedWorkflow = useCallback(async () => {
    if (!enhancedBenchResources || enhancedBenchResources.length === 0) return;
    
    try {
      setBatchLoadingComplete(false);
      
      // Step 1: Extract project allocation IDs directly from bench resources (no API call needed)
      const projectAllocationIds = enhancedBenchResources
        .map(employee => employee.projectAllocationId)
        .filter(id => id && id !== 0); // Filter out null/undefined/0 IDs
      
      const uniqueProjectAllocationIds = [...new Set(projectAllocationIds)];
      
      if (uniqueProjectAllocationIds.length === 0) {
        setBatchLoadingComplete(true);
        return;
      }
      
      // Step 2: Get task counts using the project allocation IDs we already have
      const taskCountResponse = await getActiveTaskCountsBatch(uniqueProjectAllocationIds);
      
      if (!taskCountResponse || !Array.isArray(taskCountResponse.data || taskCountResponse)) {
        setBatchLoadingComplete(true);
        return;
      }
      
      const taskCountData = taskCountResponse.data || taskCountResponse;
      
      // Step 3: Create direct employee-to-task-count mapping using the employeeId field
      const newEmployeeToTaskCountMap = new Map();
      
      // Gather Employee IDs from the bench resources for validation
      const employeeIds = enhancedBenchResources
        .map(employee => employee.employeeGuid || employee.employeeId)
        .filter(id => id);
      
      taskCountData.forEach((item, index) => {
        // Use the employeeId field directly from the enhanced API response
        if (item.employeeId && typeof item.activeTaskCount === 'number') {
          // Normalize to lowercase for consistent matching
          const normalizedEmployeeId = String(item.employeeId).toLowerCase();
          newEmployeeToTaskCountMap.set(normalizedEmployeeId, item.activeTaskCount);
        }
      });
      
      // IMPORTANT: For employees not in the API response, they have 0 tasks (no allocations)
      employeeIds.forEach(employeeId => {
        const normalizedId = String(employeeId).toLowerCase();
        if (!newEmployeeToTaskCountMap.has(normalizedId)) {
          newEmployeeToTaskCountMap.set(normalizedId, 0);
        }
      });
      
      // Update state with the direct mapping
      setEmployeeToTaskCountMap(newEmployeeToTaskCountMap);
      setBatchLoadingComplete(true);
      
    } catch (error) {
      setBatchLoadingComplete(true);
    }
  }, [enhancedBenchResources]); // useCallback dependencies

  // Trigger simplified workflow when component mounts or enhancedBenchResources changes
  useEffect(() => {
    if (enhancedBenchResources && enhancedBenchResources.length > 0) {
      fetchTaskCountsSimplifiedWorkflow();
    }
  }, [enhancedBenchResources, fetchTaskCountsSimplifiedWorkflow]); // Added missing dependency

  // =====================================================
  // SIMPLIFIED WORKFLOW PART B: On-Demand Actions
  // =====================================================
  
  // Handle task creation - Uses simplified direct lookup
  const handleCreateTask = async (employee) => {
    
    // Use employeeGuid (UUID) if available, fallback to employeeId
    const employeeIdToUse = employee.employeeGuid || employee.employeeId;
    
    if (!employeeIdToUse) {
      toast.error('Error: No employee ID available for task creation', toastOptions);
      return;
    }
    
    
    try {
      // Use projectAllocationId directly from the employee data (no API call needed)
      const projectAllocationId = employee.projectAllocationId || 0;
      
      if (!projectAllocationId || projectAllocationId === 0) {
        toast.warning('Warning: No project allocation found for this employee. The task will be created without a project allocation ID.', toastOptions);
      }
      
      //console.log('🎯 Project Allocation ID for task creation (from employee data):', projectAllocationId);
      
      // Set up the employee data with the allocation ID
      const employeeForTask = {
        name: employee.name || 'N/A',
        employeeId: employee.employeeId || 'N/A', // Display ID (readable)
        id: employeeIdToUse, // UUID for API calls
        projectAllocationId: projectAllocationId || 0 // Use projectAllocationId directly from data
      };
      
      //console.log('📝 Employee data prepared for task creation (optimized - no API call):', employeeForTask);
      
      // Open the task creation modal
      setSelectedEmployeeForTask(employeeForTask);
      setTaskModalOpen(true);
      
    } catch (error) {
      //console.error('❌ Error in hybrid workflow create task:', error);
      toast.error(`Error preparing task creation: ${error.message || 'Unknown error'}`, toastOptions);
    }
  };

  const handleCloseTaskModal = () => {
    setTaskModalOpen(false);
    setSelectedEmployeeForTask(null);
  };

  // Handle task creation success - refresh task counts
  const handleTaskCreated = async (createdTaskDetails) => {
    //console.log('✅ Task created successfully, refreshing all task counts...', createdTaskDetails);
    // Refresh the complete workflow to get updated counts for all employees
    await fetchTaskCountsSimplifiedWorkflow();
  };

  // Handle view tasks - Uses projectAllocationId directly from employee data
  const handleViewTasks = async (employee) => {
    //console.log('✅ OPTIMIZED WORKFLOW - View Tasks - Using projectAllocationId directly from employee data:', employee);
    try {
      // Use employeeGuid (UUID) if available, fallback to employeeId
      const employeeIdToUse = employee.employeeGuid || employee.employeeId;
      
      if (!employeeIdToUse) {
        //console.error('❌ No employee ID available for viewing tasks');
        toast.error('Error: No employee ID available for viewing tasks', toastOptions);
        return;
      }
      
      //console.log('👤 Using employee ID for view tasks:', employeeIdToUse);
      
      // Use projectAllocationId directly from the employee data (no API call needed)
      const projectAllocationId = employee.projectAllocationId || 0;
      
      if (!projectAllocationId || projectAllocationId === 0) {
        toast.warning('No project allocation found for this employee', toastOptions);
        
        // Still open modal but without tasks data
        setSelectedEmployeeForViewTasks({
          resourceName: employee.name || 'N/A',
          employeeId: employee.employeeId || 'N/A',
          id: employeeIdToUse,
          projectAllocationId: null,
          tasks: [],
          activeTaskCount: 0
        });
        setViewTasksModalOpen(true);
        return;
      }
      
      // Fetch tasks using the project allocation ID (from employee data, not API call)
      //console.log('📋 Fetching tasks for project allocation ID (from employee data):', projectAllocationId);
      const tasksResponse = await getTasksByProjectAllocation(projectAllocationId);
      const tasks = tasksResponse?.data || tasksResponse || [];
      
      //console.log('✅ Fetched tasks:', tasks);
      //console.log('📊 Number of tasks found:', tasks.length);
      
      // Prepare employee data with tasks and count active tasks
      const activeTaskCount = countActiveTasksForEmployee(tasks);
      //console.log('📊 Active tasks count (Open + In Progress):', activeTaskCount);
      
      const employeeWithAllocation = {
        ...employee,
        resourceName: employee.name || 'N/A',
        employeeId: employee.employeeId || 'N/A',
        id: employeeIdToUse,
        projectAllocationId: projectAllocationId,
        tasks: tasks,
        activeTaskCount: activeTaskCount
      };
      
      setSelectedEmployeeForViewTasks(employeeWithAllocation);
      setViewTasksModalOpen(true);
      
      // Show success message with task count
      if (tasks.length > 0) {
        toast.success(`Found ${tasks.length} task(s) for ${employee.name}`, toastOptions);
      } else {
        toast.info(`No tasks found for ${employee.name}`, toastOptions);
      }
      
    } catch (error) {
      //console.error('❌ Error in hybrid workflow view tasks:', error);
      
      // Fallback: open modal without project allocation ID or tasks
      setSelectedEmployeeForViewTasks({
        resourceName: employee.name || 'N/A',
        employeeId: employee.employeeId || 'N/A',
        id: employee.employeeGuid || employee.employeeId || employee.azureId,
        projectAllocationId: null,
        tasks: [],
        activeTaskCount: 0
      });
      setViewTasksModalOpen(true);
      toast.error(`Failed to fetch tasks: ${error.message || 'Unknown error'}`, toastOptions);
    }
  };

  const handleCloseViewTasksModal = () => {
    setViewTasksModalOpen(false);
    setSelectedEmployeeForViewTasks(null);
  };

  // Handle employee click for project detail view
  const handleEmployeeClick = (resource) => {
    //console.log('Employee clicked:', resource);
    //console.log('Employee ID (current):', resource.employeeId);
    //console.log('Employee GUID ID:', resource.employeeGuid);
    
    // Use the GUID employee ID if available, fallback to regular employeeId
    const employeeIdToUse = resource.employeeGuid || resource.employeeId;
    //console.log('Using employee ID:', employeeIdToUse);
    
    if (employeeIdToUse && employeeIdToUse !== 'N/A') {
      setSelectedEmployeeIdForDetail(employeeIdToUse);
      setViewProjectDetails(true);
    } else {
      //console.warn('No valid employee ID found for:', resource);
    }
  };

  const handleCloseProjectDetailModal = () => {
    setViewProjectDetails(false);
    setSelectedEmployeeIdForDetail('');
  };

  // Filter handlers
  const handleLastProjectFilterChange = (event, newValue) => {
    setSelectedLastProjects(newValue);
  };

  const handleClearFilters = () => {
    setSelectedLastProjects([]);
    setLastProjectSearchText('');
  };

  // Shadow/Non-Billable filter handlers
  const handleBillStatusFilterChange = (event, newValue) => {
    setSelectedBillStatuses(newValue);
  };

  const handleClearShadowFilters = () => {
    setSelectedBillStatuses([]);
  };

  const getFilteredProjectOptions = useMemo(() => {
    if (!lastProjectSearchText) return uniqueLastProjects;
    
    return uniqueLastProjects.filter(project =>
      project.toLowerCase().includes(lastProjectSearchText.toLowerCase())
    );
  }, [uniqueLastProjects, lastProjectSearchText]);

  // Handle empty or invalid data
  if (!enhancedBenchResources || !Array.isArray(enhancedBenchResources)) {
    return (
      <div className="bench-view">
        <div className="empty-state">
          <h3>📋 No bench resources data available</h3>
          <p>Unable to load bench resources information.</p>
        </div>
      </div>
    );
  }

  // Export functions for Excel
  const exportBenchResourcesToExcel = () => {
    const filteredData = sortedBenchResources;
    
    if (!filteredData || filteredData.length === 0) {
      toast.error('No data to export', toastOptions);
      return;
    }

    const excelData = filteredData.map(resource => ({
      'Employee Name': resource.name || 'N/A',
      'Stream': resource.stream || 'N/A',
      'Job Title': resource.jobTitle || 'N/A', 
      'Designation': resource.designation || 'N/A',
      'Skill Bucket': resource.skillBucket || 'N/A',
      'Primary Skill': resource.primarySkill || 'N/A',
      'Total Experience (Months)': resource.totalExperience || 0,
      'Last Project': resource.lastProject || 'N/A',
      'Last Allocation End Date': formatDate(getLastAllocationEndDate(resource)) || 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bench Resources');

    // Set column widths
    worksheet['!cols'] = [
      {wch: 20}, // Employee Name
      {wch: 15}, // Stream
      {wch: 15}, // Job Title
      {wch: 15}, // Designation
      {wch: 12}, // Skill Bucket
      {wch: 15}, // Primary Skill
      {wch: 12}, // Total Experience
      {wch: 20}, // Last Project
      {wch: 18}  // Last Allocation End Date
    ];

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `Bench_Resources_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(data, fileName);
    
    toast.success('Bench resources exported successfully!', toastOptions);
  };

  const exportShadowNonBillableToExcel = () => {
    const filteredData = sortedShadowNonBillableResources;
    
    if (!filteredData || filteredData.length === 0) {
      toast.error('No data to export', toastOptions);
      return;
    }

    const excelData = filteredData.map(resource => ({
      'Employee Name': resource.firstName && resource.lastName ? 
        `${resource.firstName} ${resource.lastName}` : (resource.name || 'N/A'),
      'Project Name': resource.projectName || 'N/A',
      'Bill Status': resource.billStatus || 'N/A',
      'Allocation': `${resource.billAllocation || 0}%`,
      'Stream': resource.stream || 'N/A',
      'Start Date': formatDate(resource.startDate) || 'N/A',
      'End Date': formatDate(resource.endDate) || 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Shadow Non-Billable');

    // Set column widths
    worksheet['!cols'] = [
      {wch: 20}, // Employee Name
      {wch: 20}, // Project Name
      {wch: 12}, // Bill Status
      {wch: 10}, // Allocation
      {wch: 15}, // Stream
      {wch: 12}, // Start Date
      {wch: 12}  // End Date
    ];

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `Shadow_NonBillable_Resources_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(data, fileName);
    
    toast.success('Shadow & Non-Billable resources exported successfully!', toastOptions);
  };

  return (
    <div className="bench-view">
      {/* Main View Mode Toggle */}
      <div className="main-view-toggle-section">
        <div className="main-view-toggle-container">
          <span className="main-view-label">🏁 Bench Resources</span>
          <div className="main-toggle-switch">
            <input
              type="checkbox"
              id="mainViewToggle"
              className="main-toggle-input"
              checked={mainViewMode === 'shadow-nonbillable'}
              onChange={(e) => setMainViewMode(e.target.checked ? 'shadow-nonbillable' : 'bench')}
            />
            <label htmlFor="mainViewToggle" className="main-toggle-label">
              <span className="main-toggle-slider"></span>
            </label>
          </div>
          <span className="main-view-label">⚡ Shadow & Non-Billable</span>
        </div>
      </div>


      {/* Resources Overview - Always Visible */}
      <div className="chart-section full-width">
        <h2>{mainViewMode === 'bench' ? '🏁 Bench Resources Report - Grouped by Stream' : '⚡ Shadow & Non-Billable Resources Overview'}</h2>
        <div className="bench-overview">
          <div className="bench-stats">
            {mainViewMode === 'bench' ? (
              <>
                <div className="bench-stat-card">
                  <h3>Total Bench Resources</h3>
                  <div className="stat-value">{enhancedBenchResources?.length || 0}</div>
                </div>
                <div className="bench-stat-card">
                  <h3>Streams Involved</h3>
                  <div className="stat-value">{benchStreamData?.length || 0}</div>
                </div>
                <div className="bench-stat-card">
                  <h3>Average Allocation</h3>
                  <div className="stat-value">{getAverageAllocation()}%</div>
                </div>
              </>
            ) : (
              <>
                <div className="bench-stat-card">
                  <h3>Total Shadow & Non-Billable</h3>
                  <div className="stat-value">{shadowNonBillableResources?.length || 0}</div>
                </div>
                <div className="bench-stat-card">
                  <h3>Shadow Resources</h3>
                  <div className="stat-value">{shadowNonBillableResources?.filter(r => r.billStatus?.toLowerCase() === 'shadow')?.length || 0}</div>
                </div>
                <div className="bench-stat-card">
                  <h3>Non-Billable Resources</h3>
                  <div className="stat-value">{shadowNonBillableResources?.filter(r => {
                    const status = r.billStatus?.toLowerCase() || '';
                    return status.includes('non') || status.startsWith('nb-') || status.startsWith('nb ');
                  })?.length || 0}</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Conditional Rendering Based on Main View Mode */}
      {mainViewMode === 'bench' ? (
        /* Bench Resources View */
        <>
          {/* Bench Resources Content */}
              {/* Detailed Bench Resources List */}
              <div className="chart-section full-width">
                <div className="section-header">
                  <h2>📋 Detailed Bench Resources List</h2>
                  <Button
                    variant="outlined"
                    startIcon={<img src={excelIcon} alt="Export" style={{ width: '20px', height: '20px' }} />}
                    onClick={exportBenchResourcesToExcel}
                    className="export-header-button"
                    sx={{
                      borderColor: '#1976d2',
                      color: '#1976d2',
                      '&:hover': {
                        borderColor: '#115293',
                        backgroundColor: '#f5f9ff'
                      }
                    }}
                  >
                    Export Data
                  </Button>
                </div>
            
            {/* Filter Section */}
            <div className="filter-section">
              <div className="filter-container">
                <div className="filter-title">
                  <FilterList className="filter-icon" />
                  <span>Filter by Last Project</span>
                </div>
                <div className="filter-controls">
                  <Box sx={{ flex: 1, minWidth: 300, maxWidth: 600 }}>
                    <Autocomplete
                      multiple
                      id="lastProject-filter"
                      options={getFilteredProjectOptions}
                      value={selectedLastProjects}
                      onChange={handleLastProjectFilterChange}
                      filterOptions={(options, params) => {
                        // Custom filter to allow search
                        const filtered = options.filter(option =>
                          option.toLowerCase().includes(params.inputValue.toLowerCase())
                        );
                        return filtered;
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option}
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                            sx={{
                              backgroundColor: '#667eea',
                              color: 'white',
                              margin: '1px',
                              fontSize: '0.75rem',
                              height: '24px',
                              maxWidth: '200px',
                              '& .MuiChip-deleteIcon': {
                                color: 'white',
                                fontSize: '14px',
                              },
                              '& .MuiChip-label': {
                                paddingLeft: '6px',
                                paddingRight: '2px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              },
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Select Last Projects"
                          placeholder={selectedLastProjects.length === 0 ? "Search and select projects..." : ""}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <Search sx={{ color: '#667eea', fontSize: '20px' }} />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#667eea',
                                borderWidth: '2px',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: '#666',
                              '&.Mui-focused': {
                                color: '#667eea',
                              },
                            },
                          }}
                        />
                      )}
                      sx={{
                        '& .MuiAutocomplete-tag': {
                          margin: '1px',
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          minHeight: '40px',
                          padding: '4px 8px',
                        },
                        '& .MuiAutocomplete-input': {
                          padding: '4px 0 !important',
                        },
                      }}
                    />
                  </Box>
                  {selectedLastProjects.length > 0 && (
                    <IconButton
                      onClick={handleClearFilters}
                      sx={{
                        ml: 1,
                        color: '#667eea',
                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                        border: '1px solid rgba(102, 126, 234, 0.2)',
                        borderRadius: '8px',
                        width: '40px',
                        height: '40px',
                        '&:hover': {
                          backgroundColor: 'rgba(102, 126, 234, 0.15)',
                          borderColor: '#667eea',
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                      title="Clear all filters"
                    >
                      <Clear />
                    </IconButton>
                  )}
                </div>
                {selectedLastProjects.length > 0 && (
                  <div className="filter-summary">
                    <span>
                      Showing {sortedBenchResources.length} of {enhancedBenchResources.length} resources
                      {selectedLastProjects.length === 1 
                        ? ` filtered by "${selectedLastProjects[0]}"`
                        : ` filtered by ${selectedLastProjects.length} projects`
                      }
                    </span>
                  </div>
                )}
              </div>
            </div>

            {enhancedBenchResources.length > 0 ? (
              <div className="bench-resources-table">
                <table>
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSort('name')}>
                        Employee Name <SortIndicator field="name" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('employeeId')}>
                        Emp ID <SortIndicator field="employeeId" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('stream')}>
                        Stream <SortIndicator field="stream" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('designation')}>
                        Designation <SortIndicator field="designation" />
                      </th>
                      <th>Bill Status</th>
                      <th>Allocation %</th>
                      <th className="sortable" onClick={() => handleSort('totalExperience')}>
                        Total Experience <SortIndicator field="totalExperience" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('lastProject')}>
                        Last Project <SortIndicator field="lastProject" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('lastAllocationEndDate')}>
                        Last Allocation End <SortIndicator field="lastAllocationEndDate" />
                      </th>
                      <th>Primary Skill</th>
                      <th className="actions-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedBenchResources.map((resource, index) => (
                      <tr key={index}>
                        <td 
                          className="resource-name clickable-employee-name" 
                          onClick={() => handleEmployeeClick(resource)}
                          style={{ 
                            cursor: 'pointer', 
                            color: '#1773bc',
                            fontWeight: '600'
                          }}
                          title="Click to view project allocation details"
                        >
                          {resource.name || 'N/A'}
                        </td>
                        <td className="employee-id">{resource.employeeId || 'N/A'}</td>
                        <td className="stream">{resource.stream || 'N/A'}</td>
                        <td className="designation">{resource.designation || 'N/A'}</td>
                        <td>
                          <span 
                            className="status-badge" 
                            style={{backgroundColor: COLORS[resource.billStatus] || '#ccc'}}
                          >
                            {resource.billStatus}
                          </span>
                        </td>
                        <td className="allocation">
                          {resource.allocation !== null && 
                           resource.allocation !== undefined && 
                           !isNaN(resource.allocation) && 
                           isFinite(resource.allocation) 
                            ? (resource.allocation * 100).toFixed(1) 
                            : '0.0'}%
                        </td>
                        <td className="total-experience">{formatExperience(resource.totalExperience)}</td>
                        <td className="last-project">{resource.lastProject || 'N/A'}</td>
                        <td className="last-allocation-end-date">{formatDate(getLastAllocationEndDate(resource))}</td>
                        <td className="primary-skill">{resource.primarySkill || 'N/A'}</td>
                        <td className="actions-cell">
                          <MuiTooltip title="View Previous Tasks" arrow>
                            <Badge 
                              badgeContent={getTaskCountForEmployee(resource)}
                              color="primary"
                              max={99}
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '0.7rem',
                                  minWidth: '18px',
                                  height: '18px',
                                  backgroundColor: getTaskCountForEmployee(resource) > 0 ? '#1976d2' : '#666',
                                  color: 'white'
                                }
                              }}
                            >
                              <IconButton
                                className="view-tasks-icon-btn"
                                onClick={() => handleViewTasks(resource)}
                                size="small"
                              >
                                <img src={myTasksIcon} alt="View Tasks" />
                              </IconButton>
                            </Badge>
                          </MuiTooltip>
                          <MuiTooltip title="Create Task" arrow>
                            <IconButton
                              className="task-icon-btn"
                              onClick={() => handleCreateTask(resource)}
                              size="small"
                            >
                              <img src={createTaskIcon} alt="Create Task" />
                            </IconButton>
                          </MuiTooltip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <h3>🔍 No bench resources detected</h3>
                <p>The system searched for resources with the following criteria:</p>
                <ul style={{textAlign: 'left', maxWidth: '600px', margin: '1rem auto'}}>
                  <li>Project names containing: "bench", "unallocated", "available", "free"</li>
                  <li>Bill status containing: "bench", "unallocated", "available"</li>
                  <li>Empty or generic project names: "unknown project", "n/a", "tbd"</li>
                  <li>Alternative detection: "shadow" or "non-billable" resources</li>
                </ul>
                <p>Check the browser console (F12) for detailed logging about available data.</p>
              </div>
            )}
          </div>

        </>
      ) : (
        /* Shadow & Non-Billable Resources View */
        <>
          {/* Shadow & Non-Billable Resources List */}
          <div className="chart-section full-width">
            <div className="section-header">
              <h2>⚡ Shadow & Non-Billable Resources List</h2>
              <Button
                variant="outlined"
                startIcon={<img src={excelIcon} alt="Export" style={{ width: '20px', height: '20px' }} />}
                onClick={exportShadowNonBillableToExcel}
                className="export-header-button"
                sx={{
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#115293',
                    backgroundColor: '#f5f9ff'
                  }
                }}
              >
                Export Data
              </Button>
            </div>
            
            {/* Bill Status Filter Section */}
            <div className="filter-section">
              <div className="filter-container">
                <div className="filter-title">
                  <FilterList className="filter-icon" />
                  <span>Filter by Bill Status</span>
                </div>
                <div className="filter-controls">
                  <Box sx={{ flex: 1, minWidth: 300, maxWidth: 600 }}>
                    <Autocomplete
                      multiple
                      id="billStatus-filter"
                      options={uniqueBillStatuses}
                      value={selectedBillStatuses}
                      onChange={handleBillStatusFilterChange}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option}
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                            sx={{
                              backgroundColor: COLORS[option] || '#667eea',
                              color: 'white',
                              margin: '1px',
                              fontSize: '0.75rem',
                              height: '24px',
                              maxWidth: '200px',
                              '& .MuiChip-deleteIcon': {
                                color: 'white',
                                fontSize: '14px',
                              },
                              '& .MuiChip-label': {
                                paddingLeft: '6px',
                                paddingRight: '2px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              },
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Select Bill Status"
                          placeholder={selectedBillStatuses.length === 0 ? "Search and select status..." : ""}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <Search sx={{ color: '#667eea', fontSize: '20px' }} />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#667eea',
                                borderWidth: '2px',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: '#666',
                              '&.Mui-focused': {
                                color: '#667eea',
                              },
                            },
                          }}
                        />
                      )}
                      sx={{
                        '& .MuiAutocomplete-tag': {
                          margin: '1px',
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          minHeight: '40px',
                          padding: '4px 8px',
                        },
                        '& .MuiAutocomplete-input': {
                          padding: '4px 0 !important',
                        },
                      }}
                    />
                  </Box>
                  {selectedBillStatuses.length > 0 && (
                    <IconButton
                      onClick={handleClearShadowFilters}
                      sx={{
                        ml: 1,
                        color: '#667eea',
                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                        border: '1px solid rgba(102, 126, 234, 0.2)',
                        borderRadius: '8px',
                        width: '40px',
                        height: '40px',
                        '&:hover': {
                          backgroundColor: 'rgba(102, 126, 234, 0.15)',
                          borderColor: '#667eea',
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                      title="Clear all filters"
                    >
                      <Clear />
                    </IconButton>
                  )}
                </div>
                {selectedBillStatuses.length > 0 && (
                  <div className="filter-summary">
                    <span>
                      Showing {sortedShadowNonBillableResources.length} of {shadowNonBillableResources.length} resources
                      {selectedBillStatuses.length === 1 
                        ? ` filtered by "${selectedBillStatuses[0]}"`
                        : ` filtered by ${selectedBillStatuses.length} statuses`
                      }
                    </span>
                  </div>
                )}
              </div>
            </div>

            {shadowNonBillableResources.length > 0 ? (
              <div className="bench-resources-table">
                <table>
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSort('name')}>
                        Employee Name <SortIndicator field="name" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('employeeId')}>
                        Emp ID <SortIndicator field="employeeId" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('stream')}>
                        Stream <SortIndicator field="stream" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('designation')}>
                        Designation <SortIndicator field="designation" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('projectName')}>
                        Project Name <SortIndicator field="projectName" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('billStatus')}>
                        Bill Status <SortIndicator field="billStatus" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('startDate')}>
                        Allocation Start <SortIndicator field="startDate" />
                      </th>
                      <th className="sortable" onClick={() => handleSort('endDate')}>
                        Allocation End <SortIndicator field="endDate" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedShadowNonBillableResources.map((resource, index) => (
                      <tr key={index}>
                        <td 
                          className="resource-name clickable-employee-name" 
                          onClick={() => handleEmployeeClick(resource)}
                          style={{ 
                            cursor: 'pointer', 
                            color: '#1773bc',
                            fontWeight: '600'
                          }}
                          title="Click to view project allocation details"
                        >
                          {getEmployeeName(resource)}
                        </td>
                        <td className="employee-id">{getEmployeeId(resource)}</td>
                        <td 
                          className={`stream ${(resource.stream || '').length > 35 ? 'wrap-text' : ''}`}
                          title={resource.stream || 'N/A'}
                        >
                          {resource.stream || 'N/A'}
                        </td>
                        <td className="designation">{resource.designation || 'N/A'}</td>
                        <td 
                          className={`project-name ${getProjectName(resource).length > 35 ? 'wrap-text' : ''}`}
                          title={getProjectName(resource)}
                        >
                          {getProjectName(resource)}
                        </td>
                        <td className="bill-status-cell">
                          <span 
                            className="status-badge" 
                            style={{backgroundColor: COLORS[resource.billStatus] || '#ccc'}}
                          >
                            {resource.billStatus}
                          </span>
                        </td>
                        <td className="allocation-date">{formatDate(resource.startDate)}</td>
                        <td className="allocation-date">{formatDate(resource.endDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <h3>🎉 No Shadow or Non-Billable resources found!</h3>
                <p>All resources are currently allocated with Billable status.</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Task Creation Modal */}
      <TaskCreationModal
        open={taskModalOpen}
        onClose={handleCloseTaskModal}
        selectedBenchEmployee={selectedEmployeeForTask}
        onTaskCreated={handleTaskCreated}
      />

      {/* View Tasks Modal */}
      <ViewTasksModal
        open={viewTasksModalOpen}
        onClose={handleCloseViewTasksModal}
        employee={selectedEmployeeForViewTasks}
      />

      {/* Project Allocations Detail View Modal */}
      <DetailView
        viewDetails={viewProjectDetails}
        setViewDetails={handleCloseProjectDetailModal}
        selectedEmpId={selectedEmployeeIdForDetail}
        selectedPeriodWeek={null}
        headingName={Modules.PROJECT_MANAGEMENT}
        selectedProjectAllocationId={null}
        selectedProjectId={null}
      />
    </div>
  );
};

export default BenchView;
